import { useState } from 'react';
import { useCreateExamRequest } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { type ExamPriority } from '@/api';

interface ExamRequestEditorProps {
  medicalRecordId: string;
  patientId: string;
  doctorId: string;
  appointmentId: string;
  existingExamRequests?: Array<{
    id: string;
    exams: Array<{ examName: string; justification: string; observations?: string }>;
    priority: ExamPriority;
    instructions?: string;
    isDigital: boolean;
  }>;
  onExamRequestAdded?: () => void;
  disabled?: boolean;
}

const priorityOptions: { value: ExamPriority; label: string; description: string }[] = [
  { value: 'ROUTINE', label: 'Rotina', description: 'Pode esperar' },
  { value: 'URGENT', label: 'Urgente', description: 'Resultado em até 48h' },
  { value: 'EMERGENCY', label: 'Emergência', description: 'Resultado imediato' },
];

interface ExamForm {
  examName: string;
  justification: string;
  observations: string;
}

const emptyExam: ExamForm = {
  examName: '',
  justification: '',
  observations: '',
};

export function ExamRequestEditor({
  medicalRecordId,
  patientId,
  doctorId,
  appointmentId,
  existingExamRequests = [],
  onExamRequestAdded,
  disabled = false,
}: ExamRequestEditorProps) {
  const [showForm, setShowForm] = useState(false);
  const [exams, setExams] = useState<ExamForm[]>([{ ...emptyExam }]);
  const [priority, setPriority] = useState<ExamPriority>('ROUTINE');
  const [instructions, setInstructions] = useState('');

  const createExamRequest = useCreateExamRequest();

  const addExam = () => {
    setExams([...exams, { ...emptyExam }]);
  };

  const removeExam = (index: number) => {
    if (exams.length > 1) {
      setExams(exams.filter((_, i) => i !== index));
    }
  };

  const updateExam = (index: number, field: keyof ExamForm, value: string) => {
    const updated = [...exams];
    updated[index] = { ...updated[index], [field]: value } as ExamForm;
    setExams(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (exams.some(ex => !ex.examName.trim() || !ex.justification.trim())) return;

    try {
      await createExamRequest.mutateAsync({
        medicalRecordId,
        patientId,
        doctorId,
        appointmentId,
        exams: exams.map(ex => ({
          examName: ex.examName.trim(),
          justification: ex.justification.trim(),
          observations: ex.observations.trim() || undefined,
        })),
        priority,
        instructions: instructions.trim() || undefined,
      });
      setShowForm(false);
      setExams([{ ...emptyExam }]);
      setPriority('ROUTINE');
      setInstructions('');
      onExamRequestAdded?.();
    } catch (error) {
      console.error('Failed to create exam request:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setExams([{ ...emptyExam }]);
    setPriority('ROUTINE');
    setInstructions('');
  };

  const priorityColors: Record<ExamPriority, string> = {
    ROUTINE: 'bg-gray-100 text-gray-700',
    URGENT: 'bg-orange-100 text-orange-700',
    EMERGENCY: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Solicitações de Exame</h3>
        {!showForm && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            + Novo Exame
          </Button>
        )}
      </div>

      {existingExamRequests.length > 0 && (
        <div className="space-y-4">
          {existingExamRequests.map((request) => (
            <div
              key={request.id}
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${priorityColors[request.priority]}`}>
                    {priorityOptions.find(p => p.value === request.priority)?.label}
                  </span>
                  {request.isDigital && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      Digital
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {request.exams.length} exame(s)
                </span>
              </div>

              <div className="space-y-2">
                {request.exams.map((exam, idx) => (
                  <div key={idx} className="p-3 bg-white rounded border border-gray-200">
                    <p className="font-medium text-gray-900">{exam.examName}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Justificativa:</strong> {exam.justification}
                    </p>
                    {exam.observations && (
                      <p className="text-sm text-gray-500 mt-1">
                        <strong>Obs:</strong> {exam.observations}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {request.instructions && (
                <p className="mt-3 text-sm text-gray-600 bg-white p-2 rounded border border-gray-200">
                  <strong>Instruções:</strong> {request.instructions}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Exames</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addExam}
              >
                + Adicionar Exame
              </Button>
            </div>

            {exams.map((exam, index) => (
              <div key={index} className="p-3 bg-white rounded border border-gray-200 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={exam.examName}
                      onChange={(e) => updateExam(index, 'examName', e.target.value)}
                      placeholder="Nome do exame *"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={disabled || createExamRequest.isPending}
                    />
                    <input
                      type="text"
                      value={exam.justification}
                      onChange={(e) => updateExam(index, 'justification', e.target.value)}
                      placeholder="Justificativa clínica *"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={disabled || createExamRequest.isPending}
                    />
                    <input
                      type="text"
                      value={exam.observations}
                      onChange={(e) => updateExam(index, 'observations', e.target.value)}
                      placeholder="Observações (opcional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={disabled || createExamRequest.isPending}
                    />
                  </div>
                  {exams.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeExam(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridade *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {priorityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPriority(option.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    priority === option.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  disabled={disabled || createExamRequest.isPending}
                >
                  <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{option.description}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instruções Adicionais
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Instruções para o paciente ou laboratório..."
              disabled={disabled || createExamRequest.isPending}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createExamRequest.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={exams.some(ex => !ex.examName.trim() || !ex.justification.trim()) || createExamRequest.isPending}
            >
              {createExamRequest.isPending ? 'Salvando...' : 'Solicitar Exame'}
            </Button>
          </div>
        </form>
      )}

      {!showForm && existingExamRequests.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm">Nenhuma solicitação de exame.</p>
          <p className="text-xs mt-1">Clique em "Novo Exame" para começar.</p>
        </div>
      )}
    </div>
  );
}
